import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { CheckCircle2, Shield, FileText, Award, ArrowRight, AlertCircle } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07 } },
};

export default function Trust() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const legalFields = t('trust.legal_fields', { returnObjects: true }) || [];
  const insuranceItems = t('trust.insurance_items', { returnObjects: true }) || [];
  const certItems = t('trust.cert_items', { returnObjects: true }) || [];
  const docsItems = t('trust.docs_items', { returnObjects: true }) || [];

  return (
    <>
      <Helmet>
        <title>Trust & Compliance — ENERGIESSE SL</title>
        <meta
          name="description"
          content="Legal entity details, insurance coverage, certifications, and safety standards for ENERGIESSE SL. Available for partner due diligence."
        />
      </Helmet>

      {/* Page Hero */}
      <section className="pt-14 pb-10 bg-[var(--energy-bg)] border-b border-border">
        <div className="container-site">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="max-w-3xl space-y-4"
          >
            <motion.p variants={fadeUp} className="text-xs font-medium text-[var(--energy-green)] uppercase tracking-widest">
              {t('nav.trust')}
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-heading text-3xl md:text-4xl font-semibold tracking-tight"
            >
              {t('trust.headline')}
            </motion.h1>
            <motion.p variants={fadeUp} className="text-base text-muted-foreground leading-relaxed max-w-[60ch]">
              {t('trust.subtitle')}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding bg-white" data-testid="trust-compliance-page">
        <div className="container-site space-y-14">

          {/* Legal Entity */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="space-y-4"
          >
            <motion.div variants={fadeUp} className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-[var(--energy-green)]" />
              <h2 className="font-heading font-semibold text-lg">{t('trust.legal_title')}</h2>
            </motion.div>
            <motion.div variants={fadeUp}>
              <div className="rounded-xl border border-border overflow-hidden">
                <Table>
                  <TableBody>
                    {legalFields.map((field, i) => (
                      <TableRow key={i} className="border-border">
                        <TableCell className="text-sm text-muted-foreground font-medium py-3 w-48">
                          {field.label}
                        </TableCell>
                        <TableCell className="text-sm font-medium py-3">
                          {field.value}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </motion.div>
          </motion.div>

          <Separator />

          {/* Insurance */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="space-y-4"
          >
            <motion.div variants={fadeUp} className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-[var(--energy-green)]" />
              <h2 className="font-heading font-semibold text-lg">{t('trust.insurance_title')}</h2>
            </motion.div>
            <motion.div variants={fadeUp}>
              <div className="rounded-xl border border-border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="text-xs font-medium text-muted-foreground">Coverage</TableHead>
                      <TableHead className="text-xs font-medium text-muted-foreground">Status</TableHead>
                      <TableHead className="text-xs font-medium text-muted-foreground hidden md:table-cell">Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {insuranceItems.map((item, i) => (
                      <TableRow key={i} className="border-border">
                        <TableCell className="text-sm font-medium py-3">{item.label}</TableCell>
                        <TableCell className="py-3">
                          <Badge className="bg-[var(--energy-green-soft)] text-[var(--energy-green)] border-0 text-xs">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            {item.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground py-3 hidden md:table-cell">
                          {item.detail}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </motion.div>
          </motion.div>

          <Separator />

          {/* Certifications */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="space-y-4"
          >
            <motion.div variants={fadeUp} className="flex items-center gap-2">
              <Award className="h-4 w-4 text-[var(--energy-green)]" />
              <h2 className="font-heading font-semibold text-lg">{t('trust.cert_title')}</h2>
            </motion.div>
            <motion.div variants={fadeUp}>
              <div className="rounded-xl border border-border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="text-xs font-medium text-muted-foreground">Certification</TableHead>
                      <TableHead className="text-xs font-medium text-muted-foreground">Status</TableHead>
                      <TableHead className="text-xs font-medium text-muted-foreground hidden md:table-cell">Issuer</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {certItems.map((item, i) => (
                      <TableRow key={i} className="border-border">
                        <TableCell className="text-sm font-medium py-3">{item.label}</TableCell>
                        <TableCell className="py-3">
                          <Badge variant="secondary" className="text-xs bg-[var(--energy-green-soft)] text-[var(--energy-green)] border-0">
                            {item.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground py-3 hidden md:table-cell">
                          {item.issuer}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </motion.div>
          </motion.div>

          <Separator />

          {/* Safety Standards */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="space-y-4"
          >
            <motion.div variants={fadeUp} className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-[var(--energy-green)]" />
              <h2 className="font-heading font-semibold text-lg">{t('trust.safety_title')}</h2>
            </motion.div>
            <motion.p
              variants={fadeUp}
              className="text-sm text-muted-foreground leading-relaxed max-w-[65ch]"
            >
              {t('trust.safety_desc')}
            </motion.p>
          </motion.div>

          <Separator />

          {/* Documentation deliverables */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="space-y-4"
          >
            <motion.div variants={fadeUp} className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-[var(--energy-green)]" />
              <h2 className="font-heading font-semibold text-lg">{t('trust.docs_title')}</h2>
            </motion.div>
            <motion.p variants={fadeUp} className="text-sm text-muted-foreground">
              {t('trust.docs_intro')}
            </motion.p>
            <motion.ul variants={stagger} className="space-y-2">
              {docsItems.map((doc, i) => (
                <motion.li key={i} variants={fadeUp} className="flex items-center gap-2.5 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-[var(--energy-green)] flex-shrink-0" />
                  {doc}
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>

          <Separator />

          {/* Request docs CTA */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="p-6 rounded-2xl bg-[var(--energy-bg)] border border-border space-y-3"
          >
            <h3 className="font-heading font-semibold text-base">{t('trust.request_docs')}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-[55ch]">
              {t('trust.request_docs_note')}
            </p>
            <Button
              onClick={() => navigate('/contact')}
              className="bg-[var(--energy-green)] hover:bg-[var(--energy-green)]/90 text-white btn-primary"
              data-testid="trust-request-docs-button"
            >
              {t('trust.request_docs')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </motion.div>
        </div>
      </section>
    </>
  );
}
